import React, { useState } from 'react';
import { Alert, App, Button, Card, Empty, InputNumber, Select, Space, Table, Tag, Tooltip } from 'antd';
import { CloseOutlined, DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { formatUnit } from '../../../utils/units';
import CurrencyAmount from '../../../components/CurrencyAmount';
import VariantPicker from '../../../components/inventory/VariantPicker';
import { getVariantOrderLinePatch } from '../../products/orderVariantSelection';
import { OrderDto } from '../dto';
import { ORDER_CURRENCIES } from '../currencies';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';
import { OrderProductPayload, saveOrderProducts } from '../api/orderProductsApi';
import { fetchCustomerProductPrices } from '../../customers/api/customerProductPricesApi';
import { getTotalsByCurrency, OrderProductLineDraft } from '../orderProductLineTotals';
import {
  calculateSaleQuantity,
  SALE_UNITS,
} from '../orderProductSaleQuantity';
import {
  getOrderProductDeleteCargoConflictMessage,
  getOrderProductSaveErrorMessage,
} from '../orderProductSaveErrors';
import {
  omitUnusedOrderProductHeight,
} from '../orderProductDimensions';
import {
  getOrderProductDisplayDimensions,
  getOrderProductDetailDisplayName,
  getOrderProductDisplayName,
  getOrderProductVariantLabels,
} from '../orderProductDisplay';
import './OrderProductsSection.css';

const DIMENSIONS_TAG_STYLE: React.CSSProperties = {
  marginInlineEnd: 0,
  paddingInline: 10,
  paddingBlock: 6,
  borderRadius: 999,
  background: 'linear-gradient(135deg, rgba(15, 159, 122, 0.12) 0%, rgba(15, 159, 122, 0.03) 100%)',
  border: '1px solid rgba(15, 159, 122, 0.18)',
  color: '#0f172a',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '0.01em',
};

const stripOrderProductDisplayFields = <
  T extends { productCategoryDimensionDisplayProfile?: string | null }
>(
  product: T,
): Omit<T, 'productCategoryDimensionDisplayProfile'> => {
  const { productCategoryDimensionDisplayProfile, ...apiProduct } = product;
  return apiProduct;
};

interface EditableOrderProduct {
  id: string;
  displayName?: string;
  productVariantId?: string | null;
  productCode?: string | null;
  productEan?: string | null;
  catalogProductName?: string | null;
  variantName?: string | null;
  variantAttributes?: Record<string, string>;
  variantIsDefault?: boolean;
  catalogProductWidth?: number;
  catalogProductHeight?: number;
  catalogProductLength?: number;
  catalogProductThickness?: number;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  name: string;
  quantity: number;
  saleUnit?: 'pcs' | 'm2' | 'm3';
  saleQuantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  unit?: string;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  currency?: string;
  description?: string;
  cnCode?: string;
  netWeight?: number;
  grossWeight?: number;
  countryOfOrigin?: string;
}

interface OrderProductsSectionProps {
  order: OrderDto;
  canEditProducts: boolean;
  canViewSalePrices: boolean;
  canEditSalePrices: boolean;
  salePricesIncomplete?: boolean;
  onSaved: () => Promise<unknown>;
}

const renderProductRevenueTotals = (
  products: readonly EditableOrderProduct[],
  fallbackCurrency: string,
  preferPersistedTotals = false,
) => {
  if (preferPersistedTotals) {
    const totals = products.reduce<Record<string, number>>((acc, product) => {
      const amount = Number(product.totalPrice);
      if (!Number.isFinite(amount)) return acc;
      const currency = product.currency || fallbackCurrency;
      acc[currency] = (acc[currency] || 0) + amount;
      return acc;
    }, {});
    const entries = Object.entries(totals);

    if (entries.length > 0) {
      return entries
        .map(([currency, amount]) => formatCurrency(amount, currency))
        .join(' / ');
    }
  }

  const totals = getTotalsByCurrency(
    products as readonly OrderProductLineDraft[],
    fallbackCurrency,
  );
  const entries = Object.entries(totals);

  if (entries.length === 0) {
    return formatCurrency(0, fallbackCurrency);
  }

  return entries
    .map(([currency, amount]) => formatCurrency(amount, currency))
    .join(' / ');
};

export const OrderProductsSection: React.FC<OrderProductsSectionProps> = ({
  order,
  canEditProducts,
  canViewSalePrices,
  canEditSalePrices,
  salePricesIncomplete = false,
  onSaved,
}) => {
  const { t } = useTranslation(['orders', 'common', 'products']);
  const { message, modal, notification } = App.useApp();
  const [editingProducts, setEditingProducts] = useState(false);
  const [editedProducts, setEditedProducts] = useState<EditableOrderProduct[]>([]);
  const [savingProducts, setSavingProducts] = useState(false);
  // Per-customer fixed prices keyed by variant id; powers unit-price auto-fill.
  const [customerPriceByVariant, setCustomerPriceByVariant] = useState<
    Map<string, { fixedPrice: number; currency: string }>
  >(new Map());

  const hasProductData = order.productsLoaded;
  const orderProducts = hasProductData ? order.products : [];
  const handleEditProducts = async () => {
    if (!hasProductData) {
      message.warning(t('orders:orderDetail.messages.productsUnavailable'));
      return;
    }

    const nextProducts = order.products.map((product) => ({
      ...product,
      displayName: getOrderProductDisplayName(product, product.name),
      currency: product.currency || order.currency || 'PLN',
    }));
    setEditedProducts(nextProducts);
    setEditingProducts(true);

    if (order.customerId && canViewSalePrices) {
      try {
        const prices = await fetchCustomerProductPrices(order.customerId);
        setCustomerPriceByVariant(
          new Map(
            prices.map((price) => [
              price.productVariantId,
              { fixedPrice: price.fixedPrice, currency: price.currency }
            ])
          )
        );
      } catch (error) {
        // Non-critical. Without prices, unit price stays manual.
      }
    }
  };

  const handleCancelEditProducts = () => {
    setEditingProducts(false);
    setEditedProducts([]);
  };

  const handleAddProduct = () => {
    const newProduct: EditableOrderProduct = {
      id: `new-${Date.now()}`,
      name: '',
      quantity: 1,
      saleUnit: 'pcs',
      unitPrice: 0,
      currency: order.currency || 'PLN',
      unit: 'pcs',
    };
    setEditedProducts((current) => [
      ...current,
      newProduct,
    ]);
  };

  const handleRemoveProduct = (productId: string) => {
    setEditedProducts((current) => current.filter((product) => product.id !== productId));
  };

  const handleSaleUnitChange = async (productIndex: number, nextSaleUnit: 'pcs' | 'm2' | 'm3') => {
    const currentProduct = editedProducts[productIndex];
    if (!currentProduct) {
      return;
    }

    const currentSaleUnit = currentProduct.saleUnit || 'pcs';
    if (currentSaleUnit === nextSaleUnit) {
      return;
    }

    const unitPrice = Number(currentProduct.unitPrice || 0);
    if (canViewSalePrices && unitPrice > 0) {
      const confirmed = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: t('orders:orderDetail.saleUnitChangeConfirmTitle'),
          content: t('orders:orderDetail.saleUnitChangeConfirmDescription', {
            currentUnit: formatUnit(currentSaleUnit),
            nextUnit: formatUnit(nextSaleUnit),
          }),
          okText: t('orders:orderDetail.saleUnitChangeConfirmOk'),
          cancelText: t('common:cancel'),
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });

      if (!confirmed) {
        return;
      }
    }

    const updated = [...editedProducts];
    const nextProduct = {
      ...updated[productIndex],
      saleUnit: nextSaleUnit,
    };

    updated[productIndex] = nextProduct;
    setEditedProducts(updated);
  };

  const handleSaveProducts = async () => {
    const hasInvalidProducts = editedProducts.some((product) => {
      const name = (product.name || '').trim();
      const quantity = Number(product.quantity);
      const unitPrice = Number(product.unitPrice ?? 0);

      return !name || !Number.isFinite(quantity) || quantity <= 0 || (canEditSalePrices && (!Number.isFinite(unitPrice) || unitPrice < 0));
    });

    if (hasInvalidProducts) {
      message.error(t('orders:orderDetail.messages.invalidProductData'));
      return;
    }

    setSavingProducts(true);

    try {
      const payload: OrderProductPayload[] = editedProducts.map((product) => {
        const basePayload = {
          id: product.id?.startsWith('new-') ? undefined : product.id,
          productVariantId: product.productVariantId ?? null,
          name: product.name.trim(),
          quantity: product.quantity,
          unit: product.saleUnit || product.unit || 'pcs',
          width: product.width,
          height: product.height,
          length: product.length,
          thickness: product.thickness,
          productCategoryDimensionDisplayProfile: product.productCategoryDimensionDisplayProfile,
          description: product.description,
          cnCode: product.cnCode,
          netWeight: product.netWeight,
          grossWeight: product.grossWeight,
          countryOfOrigin: product.countryOfOrigin,
        };

        if (!canEditSalePrices) {
          return stripOrderProductDisplayFields(omitUnusedOrderProductHeight(basePayload));
        }

        return stripOrderProductDisplayFields(omitUnusedOrderProductHeight({
          ...basePayload,
          saleUnit: product.saleUnit || 'pcs',
          unitPrice: product.unitPrice ?? 0,
          currency: product.currency || order.currency || 'PLN',
        }));
      });

      await saveOrderProducts(order.id, payload);
      message.success(t('orders:orderDetail.productsUpdated'));
      setEditingProducts(false);
      setEditedProducts([]);
      await onSaved();
    } catch (error) {
      // S32: a blocked cargo-referenced-line delete (400
      // ORDER_PRODUCT_HAS_TRANSPORT_CARGO) gets a Polish-first, route/product
      // -specific message; every other 4xx/5xx falls through to the existing
      // generic handling.
      const cargoConflictMessage = getOrderProductDeleteCargoConflictMessage(t, error);
      if (cargoConflictMessage) {
        // The backend rejected the whole replace transaction. Put every
        // persisted line back into the draft while preserving edits/new lines
        // that were not part of the failed removal.
        setEditedProducts((current) => {
          const currentById = new Map(current.map((product) => [product.id, product]));
          const restoredPersisted = order.products.map((product) => currentById.get(product.id) ?? {
            ...product,
            displayName: getOrderProductDisplayName(product, product.name),
            currency: product.currency || order.currency || 'PLN',
          });
          const newProducts = current.filter((product) => product.id.startsWith('new-'));
          return [...restoredPersisted, ...newProducts];
        });
        notification.error({
          message: t('orders:orderDetail.messages.productHasTransportCargoTitle'),
          description: cargoConflictMessage,
          duration: 8,
        });
      } else {
        message.error(getOrderProductSaveErrorMessage(error, t('orders:orderDetail.messages.updateError')));
      }
    } finally {
      setSavingProducts(false);
    }
  };

  const renderEditableProductRow = (product: EditableOrderProduct, index: number) => {
    const dimensions = getOrderProductDisplayDimensions(product);
    const displayName = getOrderProductDisplayName(product, product.name);
    const saleQuantity = calculateSaleQuantity(product) ?? 0;
    const currency = product.currency || order.currency || 'PLN';

    return (
      <div className="order-products-editor__row" key={product.id}>
        <div className="order-products-editor__field order-products-editor__field--product">
          <span className="order-products-editor__label">
            {t('orders:orderDetail.product')}
          </span>
          <div className="order-products-editor__product-identity">
            <div className="order-products-editor__product-copy">
              <Tooltip
                title={displayName || undefined}
                trigger={['hover', 'focus']}
              >
                <strong
                  className={`order-products-editor__product-name${displayName ? '' : ' is-empty'}`}
                  tabIndex={displayName ? 0 : undefined}
                  aria-label={displayName || undefined}
                >
                  {displayName || t('orders:orderDetail.variantPickerPlaceholder')}
                </strong>
              </Tooltip>
              {dimensions && (
                <span className="order-products-editor__product-dimensions">
                  {dimensions}
                </span>
              )}
            </div>
            <div className="order-products-editor__product-picker">
              <VariantPicker
                allowCreate
                size="middle"
                value={product.productVariantId || undefined}
                unlinkedLabel={displayName}
                triggerLabel={product.productVariantId
                  ? t('orders:orderDetail.variantPickerChange')
                  : t('orders:orderDetail.variantPickerPlaceholder')}
                placeholder={t('orders:orderDetail.variantPickerPlaceholder')}
                showInventory={false}
                style={{ width: '100%' }}
                onChange={(variantId, variant) => {
                  const updated = [...editedProducts];
                  const fixedPrice = canViewSalePrices && variantId
                    ? customerPriceByVariant.get(variantId)
                    : undefined;
                  const nextProduct = omitUnusedOrderProductHeight({
                    ...updated[index],
                    ...getVariantOrderLinePatch(
                      variantId,
                      variant,
                      updated[index],
                      fixedPrice,
                    ),
                  });
                  updated[index] = {
                    ...nextProduct,
                    displayName: getOrderProductDisplayName(nextProduct, nextProduct.name),
                  };
                  setEditedProducts(updated);
                  if (fixedPrice) {
                    message.success(t('orders:orderDetail.customerPriceApplied'));
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className="order-products-editor__field order-products-editor__field--quantity">
          <span className="order-products-editor__label">
            {t('orders:orderDetail.quantity')}
          </span>
          <InputNumber
            size="large"
            min={0.001}
            max={1000000}
            step={0.001}
            precision={3}
            value={product.quantity}
            aria-label={t('orders:orderDetail.quantity')}
            onChange={(value) => {
              const updated = [...editedProducts];
              const parsed = Number(value);
              updated[index] = {
                ...updated[index],
                quantity: Number.isFinite(parsed) ? parsed : 0,
              };
              setEditedProducts(updated);
            }}
            style={{ width: '100%' }}
          />
        </div>

        <div className="order-products-editor__field order-products-editor__field--unit">
          <span className="order-products-editor__label">
            {canViewSalePrices ? t('orders:orderDetail.saleUnit') : t('orders:orderDetail.unit')}
          </span>
          <Select
            size="large"
            value={product.saleUnit || 'pcs'}
            onChange={(value) => handleSaleUnitChange(index, value)}
            style={{ width: '100%' }}
            aria-label={t('orders:orderDetail.saleUnit')}
          >
            {SALE_UNITS.map((option) => (
              <Select.Option key={option} value={option}>
                {formatUnit(option)}
              </Select.Option>
            ))}
          </Select>
        </div>

        {canViewSalePrices && (
          <>
            <div className="order-products-editor__field order-products-editor__field--price">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.unitPriceWithSaleUnit')}
              </span>
              <InputNumber
                size="large"
                min={0}
                max={1000000}
                step={0.01}
                addonAfter={`/ ${formatUnit(product.saleUnit || 'pcs')}`}
                value={product.unitPrice ?? 0}
                aria-label={t('orders:orderDetail.unitPriceWithSaleUnit')}
                onChange={(value) => {
                  const updated = [...editedProducts];
                  const parsed = Number(value);
                  updated[index] = {
                    ...updated[index],
                    unitPrice: Number.isFinite(parsed) ? parsed : 0,
                  };
                  setEditedProducts(updated);
                }}
                style={{ width: '100%' }}
              />
            </div>

            <div className="order-products-editor__field order-products-editor__field--currency">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.currency')}
              </span>
              <Select
                size="large"
                value={currency}
                onChange={(value) => {
                  const updated = [...editedProducts];
                  updated[index] = {
                    ...updated[index],
                    currency: value,
                  };
                  setEditedProducts(updated);
                }}
                style={{ width: '100%' }}
                aria-label={t('orders:orderDetail.currency')}
              >
                {ORDER_CURRENCIES.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="order-products-editor__field order-products-editor__field--total">
              <span className="order-products-editor__label">
                {t('orders:orderDetail.saleTotal')}
              </span>
              <strong className="order-products-editor__total-value">
                {formatCurrency(saleQuantity * (product.unitPrice || 0), currency)}
              </strong>
            </div>
          </>
        )}

        <div className="order-products-editor__remove">
          <Tooltip title={t('common:delete')}>
            <Button
              danger
              type="text"
              size="large"
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveProduct(product.id)}
              aria-label={t('common:delete')}
            />
          </Tooltip>
        </div>
      </div>
    );
  };

  const productColumns = [
    {
      title: t('orders:orderDetail.product'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: EditableOrderProduct) => {
        if (record.productVariantId) {
          const displayName = getOrderProductDetailDisplayName(record, name);
          const variantLabels = getOrderProductVariantLabels(
            record,
            t('products:catalogPicker.basicProduct'),
          );
          return (
            <div className="order-product-identity">
              <span className="order-product-identity__name">{displayName}</span>
              <Space size={[4, 4]} wrap className="order-product-identity__labels">
                {variantLabels.map((label) => (
                  <Tag
                    key={`${label.key || 'variant'}:${label.value}`}
                    color={label.isBaseProduct ? 'blue' : undefined}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {label.key ? `${label.key}: ${label.value}` : label.value}
                  </Tag>
                ))}
                <Tooltip title={t('orders:orderDetail.variantLinkedHint')}>
                  <Tag bordered={false} color="green" icon={<LinkOutlined />} style={{ marginInlineEnd: 0 }}>
                    {t('orders:orderDetail.variantLinked')}
                  </Tag>
                </Tooltip>
              </Space>
            </div>
          );
        }
        return getOrderProductDisplayName(record, name);
      },
    },
    {
      title: t('orders:orderDetail.dimensions'),
      key: 'dimensions',
      render: (record: EditableOrderProduct) => {
        const dimensions = getOrderProductDisplayDimensions(record);

        return dimensions ? (
          <Tag bordered={false} style={DIMENSIONS_TAG_STYLE}>
            {dimensions}
          </Tag>
        ) : (
          '-'
        );
      },
    },
    {
      title: t('orders:orderDetail.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity: number) => quantity,
    },
    {
      title: canViewSalePrices ? t('orders:orderDetail.saleUnit') : t('orders:orderDetail.unit'),
      dataIndex: 'saleUnit',
      key: 'saleUnit',
      width: 116,
      render: (saleUnit: 'pcs' | 'm2' | 'm3' | undefined, record: EditableOrderProduct) => (
        formatUnit(saleUnit || record.unit || 'pcs')
      ),
    },
    ...(canViewSalePrices ? [
      {
        title: t('orders:orderDetail.unitPrice'),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        render: (unitPrice: number | undefined, record: EditableOrderProduct) => {
          const lineCurrency = record.currency || order.currency || 'PLN';
          const orderCurrency = order.currency || 'PLN';
          const lineRate = lineCurrency === orderCurrency ? (order.exchangeRate ?? null) : null;
          return (
            <Space size={4}>
              <CurrencyAmount
                amount={unitPrice ?? 0}
                currency={lineCurrency}
                rate={lineRate}
                rateDate={order.exchangeRateEffectiveDate || order.orderDate}
                rateSource={order.exchangeRateSource}
                rateTableNumber={order.exchangeRateTableNumber}
              />
              <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                / {formatUnit(record.saleUnit || record.unit)}
              </span>
            </Space>
          );
        },
      },
      {
        title: t('orders:orderDetail.currency'),
        dataIndex: 'currency',
        key: 'currency',
        width: 112,
        render: (currency: string | undefined) => currency || order.currency || 'PLN',
      },
      {
        title: t('orders:orderDetail.saleTotal'),
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        render: (totalPrice: number | undefined, record: EditableOrderProduct) => {
          const lineCurrency = record.currency || order.currency || 'PLN';
          const orderCurrency = order.currency || 'PLN';
          const lineRate = lineCurrency === orderCurrency ? (order.exchangeRate ?? null) : null;
          return (
            <CurrencyAmount
              amount={totalPrice ?? 0}
              currency={lineCurrency}
              rate={lineRate}
              rateDate={order.exchangeRateEffectiveDate || order.orderDate}
              rateSource={order.exchangeRateSource}
              rateTableNumber={order.exchangeRateTableNumber}
            />
          );
        },
      },
    ] : []),
  ];

  return (
    <Card
      title={renderSectionTitle(canViewSalePrices ? t('orders:orderDetail.sales') : t('orders:orderDetail.products'))}
      style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      extra={
        canEditProducts && (
          editingProducts ? (
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                loading={savingProducts}
                onClick={handleSaveProducts}
              >
                {t('orders:orderDetail.saveProducts')}
              </Button>
              <Button
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCancelEditProducts}
                disabled={savingProducts}
              >
                {t('orders:orderDetail.cancelEdit')}
              </Button>
            </Space>
          ) : (
            <Tooltip title={!hasProductData ? t('orders:orderDetail.messages.productsUnavailable') : undefined}>
              <span>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleEditProducts}
                  disabled={!hasProductData}
                >
                  {t('orders:orderDetail.editProducts')}
                </Button>
              </span>
            </Tooltip>
          )
        )
      }
    >
      {!hasProductData && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.messages.productsUnavailable')}
          style={{ marginBottom: 16 }}
        />
      )}
      {canViewSalePrices && salePricesIncomplete && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:orderDetail.salePricesIncompleteTitle')}
          description={t('orders:orderDetail.salePricesIncompleteDescription')}
          action={
            canEditProducts && !editingProducts ? (
              <Button size="small" type="primary" onClick={handleEditProducts}>
                {t('orders:orderDetail.editProducts')}
              </Button>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {editingProducts ? (
        <div
          className={`order-products-editor${canViewSalePrices ? '' : ' order-products-editor--without-prices'}`}
          data-testid="order-products-editor"
        >
          <div className="order-products-editor__list">
            {editedProducts.length > 0 ? (
              editedProducts.map(renderEditableProductRow)
            ) : (
              <Empty
                className="order-products-editor__empty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('common:messages.noData')}
              />
            )}
          </div>

          {canViewSalePrices && editedProducts.length > 0 && (
            <div className="order-products-editor__summary">
              <span>{t('orders:orderDetail.total')}</span>
              <strong>
                {renderProductRevenueTotals(editedProducts, order.currency || 'PLN')}
              </strong>
            </div>
          )}
        </div>
      ) : (
        <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
          <Table
            dataSource={orderProducts}
            columns={productColumns}
            rowKey="id"
            size="middle"
            pagination={false}
            scroll={{ x: canViewSalePrices ? 1040 : 620 }}
            summary={canViewSalePrices ? (data) => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <strong>{t('orders:orderDetail.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={5} />
                <Table.Summary.Cell index={6}>
                  <strong>{renderProductRevenueTotals(data as readonly EditableOrderProduct[], order.currency || 'PLN', true)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : undefined}
          />
        </div>
      )}

      {editingProducts && canEditProducts && (
        <Button
          type="dashed"
          onClick={handleAddProduct}
          icon={<PlusOutlined />}
          className="order-products-editor__add"
        >
          {t('orders:orderDetail.addProduct')}
        </Button>
      )}
    </Card>
  );
};
